import React from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import * as Sentry from "@sentry/react";
import { AuthProvider, useAuth } from "./lib/auth";
import "@questlabs/react-sdk/dist/style.css";

// Pages
import Landing from "./pages/Landing";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import Dashboard from "./pages/Dashboard";
import TriageCenter from "./pages/TriageCenter";
import PatientRoster from "./pages/PatientRoster";
import AdminDashboard from "./pages/AdminDashboard";
import PatientDashboard from "./pages/PatientDashboard";
import CaregiverDashboard from "./pages/CaregiverDashboard";
import DeviceSimulator from "./pages/DeviceSimulator";
import FounderPanel from "./pages/FounderPanel";
import ComplianceHub from "./pages/ComplianceHub";
import AuditorEnrollment from "./pages/AuditorEnrollment";
import AdminInviteEnrollment from "./pages/AdminInviteEnrollment";
import ErrorBoundary from "./components/ErrorBoundary";
import AppLayout from "./components/layout/AppLayout";
import VerifyEmail from "./pages/VerifyEmail";
import DemographicVerificationCenter from "./components/DemographicVerificationCenter";

const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading } = useAuth();

  if (loading)
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary-500 rounded-full" />
      </div>
    );
  if (!user) return <Navigate to="/signin" replace />;
  if (roles.length > 0 && !roles.includes(user.role))
    return <Navigate to="/" replace />;

  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/auditor/enrollment" element={<AuditorEnrollment />} />
            <Route path="/admin/invite" element={<AdminInviteEnrollment />} />
            <Route path="/verify" element={<VerifyEmail />} />

            <Route element={<AppLayout />}>
              {/* /dashboard remains a valid, registered route — old
                  bookmarks continue to resolve. It now renders the
                  lighter Clinical Dashboard Overview instead of the
                  triage-focused view that used to live here; that view
                  has moved to /dashboard/triage below. No redirect chain
                  was needed since the path itself was never removed. */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute roles={["clinician", "admin"]}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              {/* New: Triage Centre gets its own dedicated route. */}
              <Route
                path="/dashboard/triage"
                element={
                  <ProtectedRoute roles={["clinician", "admin"]}>
                    <TriageCenter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/roster"
                element={
                  <ProtectedRoute roles={["clinician", "admin"]}>
                    <PatientRoster />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/demographics"
                element={
                  <ProtectedRoute roles={["clinician", "admin"]}>
                    <DemographicVerificationCenter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient"
                element={
                  <ProtectedRoute roles={["patient"]}>
                    <PatientDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/caregiver"
                element={
                  <ProtectedRoute roles={["caregiver"]}>
                    <CaregiverDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/founder"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <FounderPanel />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance"
                element={
                  <ProtectedRoute roles={["admin", "auditor"]}>
                    <ComplianceHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simulator"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <DeviceSimulator />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
